(() => {
  const pickerSelector = '[data-product-family-picker]';
  const linkSelector = '[data-product-family-link]';
  const maxCachedSections = 6;
  const maxViewportPrefetches = 2;
  const maxImagePreloads = 3;
  const sectionCache = new Map();
  const imagePreloads = new Map();
  const observedPickers = new WeakSet();
  let viewportSectionPrefetches = 0;
  let viewportImagePreloads = 0;
  let requestToken = 0;

  function cleanDuplicateAssets() {
    ['SakuraProductFamilyPickerStyles', 'SakuraProductFamilyPickerScript'].forEach((id) => {
      const assets = document.querySelectorAll(`#${id}`);
      assets.forEach((asset, index) => {
        if (index > 0) asset.remove();
      });
    });
  }

  function navigateNormally(url) {
    window.location.assign(url);
  }

  const managedMetadata = new Set([
    'name:description',
    'property:og:site_name', 'property:og:url', 'property:og:title',
    'property:og:type', 'property:og:description',
    'property:og:image', 'property:og:image:secure_url',
    'property:og:image:width', 'property:og:image:height',
    'property:og:price:amount', 'property:og:price:currency',
    'name:twitter:site', 'name:twitter:card',
    'name:twitter:title', 'name:twitter:description',
  ]);
  const requiredMetadata = [
    'property:og:site_name', 'property:og:url', 'property:og:title',
    'property:og:type', 'property:og:description',
    'property:og:price:amount', 'property:og:price:currency',
    'name:twitter:card', 'name:twitter:title', 'name:twitter:description',
  ];

  function metadataKey(element) {
    const property = element.getAttribute('property');
    const name = element.getAttribute('name');
    if (property && name) return null;
    return property ? 'property:' + property : name ? 'name:' + name : null;
  }

  function productIdentity(url) {
    const parsed = new URL(url, window.location.href);
    if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error('Invalid product URL.');
    parsed.search = '';
    parsed.hash = '';
    return parsed.href.replace(/%[0-9a-f]{2}/gi, (triplet) => triplet.toUpperCase());
  }

  function readProductMetadata(parsedDocument, section, productId, destinationUrl) {
    const currentLink = section.querySelector(linkSelector + '.is-current');
    if (currentLink?.dataset.productId !== productId
      || productIdentity(currentLink.href) !== productIdentity(destinationUrl)) {
      throw new Error('The returned section is for a different product.');
    }
    const titles = parsedDocument.head.querySelectorAll('title');
    const canonicals = parsedDocument.head.querySelectorAll('link[rel="canonical"]');
    const canonical = canonicals[0]?.getAttribute('href');
    const metas = new Map();
    for (const element of parsedDocument.head.querySelectorAll('meta')) {
      const key = metadataKey(element);
      if (!managedMetadata.has(key)) continue;
      if (metas.has(key) || !element.hasAttribute('content')) {
        throw new Error('Product metadata is duplicated or incomplete.');
      }
      metas.set(key, {
        attribute: element.hasAttribute('property') ? 'property' : 'name',
        name: element.getAttribute('property') || element.getAttribute('name'),
        content: element.getAttribute('content'),
      });
    }
    if (titles.length !== 1 || !parsedDocument.title.trim() || canonicals.length !== 1
      || typeof canonical !== 'string'
      || productIdentity(canonical) !== productIdentity(destinationUrl)
      || new URL(canonical, window.location.href).origin !== new URL(window.location.href).origin
      || requiredMetadata.some((key) => !metas.has(key))
      || metas.get('property:og:url').content !== canonical
      || metas.get('property:og:type').content !== 'product') {
      throw new Error('Product metadata does not match the selected product.');
    }
    return { title: parsedDocument.title, canonical, metas: [...metas.values()] };
  }

  function currentManagedMetadata() {
    return [...document.head.querySelectorAll('meta')]
      .filter((element) => managedMetadata.has(metadataKey(element)));
  }

  function captureHead() {
    const canonicals = document.head.querySelectorAll('link[rel="canonical"]');
    if (canonicals.length !== 1) throw new Error('Current canonical is missing or duplicated.');
    return {
      title: document.title,
      canonical: canonicals[0].getAttribute('href'),
      metas: currentManagedMetadata().map((element) => ({
        attribute: element.hasAttribute('property') ? 'property' : 'name',
        name: element.getAttribute('property') || element.getAttribute('name'),
        content: element.getAttribute('content'),
      })),
    };
  }

  function applyHead(metadata) {
    // Create detached tags first; never copy scripts or unrelated head elements.
    const metas = metadata.metas.map((value) => {
      const element = document.createElement('meta');
      element.setAttribute(value.attribute, value.name);
      element.setAttribute('content', value.content);
      return element;
    });
    const canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) throw new Error('Current canonical is missing.');
    document.title = metadata.title;
    canonical.setAttribute('href', metadata.canonical);
    for (const element of currentManagedMetadata()) element.remove();
    for (const element of metas) document.head.appendChild(element);
  }

  function viewportTop(element) {
    try {
      const top = element?.getBoundingClientRect?.().top;
      return Number.isFinite(top) ? top : null;
    } catch (error) {
      return null;
    }
  }

  function compensateScroll(previousTop, updatedTop) {
    const delta = updatedTop - previousTop;
    if (previousTop === null || updatedTop === null || !Number.isFinite(delta) || Math.abs(delta) < 1 || typeof window.scrollBy !== 'function') return;
    try {
      window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
    } catch (error) {
      // Geometry and scrolling support must never turn a successful swap into navigation.
    }
  }

  function preserveLayoutHeights(currentSection, updatedSection) {
    ['.product__media-wrapper', '.product__info-container > .product__title'].forEach((selector) => {
      const currentElement = currentSection?.querySelector?.(selector);
      const updatedElement = updatedSection?.querySelector?.(selector);
      const currentHeight = currentElement?.getBoundingClientRect?.().height;
      if (!updatedElement || !Number.isFinite(currentHeight) || currentHeight <= 0) return;
      updatedElement.style.minHeight = `${Math.ceil(currentHeight)}px`;
    });
  }

  function setExpanded(picker, expanded) {
    const toggle = picker?.querySelector?.('[data-product-family-expand]');
    if (!picker || !toggle) return;
    picker.classList.toggle('is-expanded', expanded);
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.textContent = expanded ? toggle.dataset.expandedLabel : toggle.dataset.collapsedLabel;
  }

  function refreshRelatedProducts(productId) {
    let recommendations;
    try {
      recommendations = document.querySelector('product-recommendations.related-products');
      if (!recommendations) return;
      if (!productId) throw new Error('The selected product ID is missing.');
      if (!recommendations.dataset.url) throw new Error('The recommendations URL is missing.');
      const requestUrl = new URL(recommendations.dataset.url, window.location.href);
      requestUrl.searchParams.set('product_id', productId);
      // A fresh element starts Dawn's lazy loader for the selected product.
      // Removing the old element also isolates any request it already started.
      const replacement = recommendations.cloneNode(false);
      replacement.dataset.url = requestUrl.href;
      recommendations.replaceWith(replacement);
    } catch (error) {
      // A failed recommendations refresh must not leave cards for the old product.
      try {
        recommendations?.remove();
      } catch (removeError) {
        // Recommendations are optional; keep the selected product visible.
      }
    }
  }

  function getSectionRequestUrl(destinationUrl) {
    const requestUrl = new URL(destinationUrl, window.location.href);
    // One normal PDP response supplies both the real SSR head and product section.
    requestUrl.searchParams.delete('section_id');
    return requestUrl.href;
  }

  function cacheSection(requestUrl, sectionPromise) {
    sectionCache.delete(requestUrl);
    sectionCache.set(requestUrl, sectionPromise);
    while (sectionCache.size > maxCachedSections) {
      sectionCache.delete(sectionCache.keys().next().value);
    }
  }

  function seedCurrentSection(root = document) {
    const pickers = root.matches?.(pickerSelector) ? [root] : root.querySelectorAll(pickerSelector);
    pickers.forEach((picker) => {
      const sectionId = picker.dataset.sectionId;
      const currentLink = picker.querySelector(`${linkSelector}.is-current`);
      const section = sectionId && document.getElementById(`MainProduct-${sectionId}`);
      if (!currentLink || !section) return;
      const requestUrl = getSectionRequestUrl(currentLink.href, sectionId);
      if (sectionCache.has(requestUrl)) return;

      // Shopify replaces the pickup-availability template after it loads. Caching
      // that mutated custom element makes it throw when the section is restored.
      const initializedPickup = section.querySelector('pickup-availability[available]');
      if (initializedPickup && !initializedPickup.querySelector('template')) {
        getProductSection(currentLink.href, sectionId).catch(() => {});
        return;
      }

      cacheSection(requestUrl, Promise.resolve(document.documentElement.outerHTML));
    });
  }

  async function getProductSection(destinationUrl, sectionId) {
    const requestUrl = getSectionRequestUrl(destinationUrl, sectionId);
    if (!sectionCache.has(requestUrl)) {
      cacheSection(
        requestUrl,
        fetch(requestUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        }).then((response) => {
          if (!response.ok) throw new Error(`Product section request failed: ${response.status}`);
          return response.text();
        })
      );
    }

    const cachedSection = sectionCache.get(requestUrl);
    try {
      return await cachedSection;
    } catch (error) {
      if (sectionCache.get(requestUrl) === cachedSection) sectionCache.delete(requestUrl);
      throw error;
    }
  }

  function allowSpeculation() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !connection?.saveData && !/(^|-)2g$/.test(connection?.effectiveType || '');
  }

  function prefetchProductSection(link) {
    const picker = link?.closest(pickerSelector);
    const sectionId = picker?.dataset.sectionId;
    if (!picker || !sectionId || link.classList.contains('is-current') || !allowSpeculation()) return;
    getProductSection(link.href, sectionId).catch(() => {});
  }

  function preloadFeaturedImage(link, fromViewport = false) {
    if (!allowSpeculation() || !link || link.classList.contains('is-current')) return;
    if (fromViewport && viewportImagePreloads >= 1) return;
    if (imagePreloads.size >= maxImagePreloads) return;
    const source = link.dataset.featuredImageUrl;
    if (!source) return;
    const url = new URL(source, window.location.href);
    if (!['http:', 'https:'].includes(url.protocol) || imagePreloads.has(url.href)) return;
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'low';
    image.src = url.href;
    imagePreloads.set(url.href, image);
    if (fromViewport) viewportImagePreloads += 1;
  }

  function prefetchIntent(link) {
    prefetchProductSection(link);
    preloadFeaturedImage(link);
  }

  function scheduleViewportPrefetch(root = document) {
    if (!allowSpeculation()) return;
    const pickers = root.matches?.(pickerSelector) ? [root] : root.querySelectorAll(pickerSelector);
    pickers.forEach((picker) => {
      if (observedPickers.has(picker)) return;
      observedPickers.add(picker);
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        const run = () => {
          const candidates = [...picker.querySelectorAll(`${linkSelector}:not(.is-current)`)]
            .filter((link) => !sectionCache.has(getSectionRequestUrl(link.href, picker.dataset.sectionId)))
            .slice(0, maxViewportPrefetches - viewportSectionPrefetches);
          if (candidates[0]) preloadFeaturedImage(candidates[0], true);
          candidates.forEach((link, index) => {
            viewportSectionPrefetches += 1;
            window.setTimeout(() => {
              if (picker.isConnected) prefetchProductSection(link);
            }, index * 150);
          });
        };
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(run, { timeout: 1000 });
        } else {
          run();
        }
      }, { rootMargin: '120px 0px' });
      observer.observe(picker);
    });
  }

  document.addEventListener('pointerover', (event) => {
    prefetchIntent(event.target.closest?.(linkSelector));
  });
  document.addEventListener('focusin', (event) => {
    prefetchIntent(event.target.closest?.(linkSelector));
  });
  document.addEventListener('touchstart', (event) => {
    prefetchIntent(event.target.closest?.(linkSelector));
  }, { passive: true });

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.('[data-product-family-expand]');
    if (!toggle) return;
    const picker = toggle.closest(pickerSelector);
    if (!picker) return;
    setExpanded(picker, toggle.getAttribute('aria-expanded') !== 'true');
  });

  document.addEventListener('click', async (event) => {
    const link = event.target.closest?.(linkSelector);
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const picker = link.closest(pickerSelector);
    const sectionId = picker?.dataset.sectionId;
    const currentSection = sectionId && document.getElementById(`MainProduct-${sectionId}`);
    if (!picker || !currentSection || link.target || link.hasAttribute('download')) return;

    event.preventDefault();
    seedCurrentSection(picker);
    const token = ++requestToken;
    const destinationUrl = new URL(link.href, window.location.href).href;
    const selectedProductId = link.dataset.productId;
    const wasExpanded = picker.classList.contains('is-expanded');
    const previousUrl = window.location.href;
    let previousHead;
    let updatedSection;
    let sectionReplaced = false;
    let committed = false;
    const loadingTimer = window.setTimeout(() => {
      if (token === requestToken && picker.isConnected) {
        picker.classList.add('is-loading');
        picker.setAttribute('aria-busy', 'true');
        link.classList.add('is-loading');
      }
    }, 180);

    try {
      const responseText = await getProductSection(destinationUrl, sectionId);
      const parsedDocument = new DOMParser().parseFromString(responseText, 'text/html');
      updatedSection = parsedDocument.getElementById(`MainProduct-${sectionId}`);
      const updatedLink = updatedSection?.querySelector(`${linkSelector}[data-product-id="${CSS.escape(selectedProductId)}"]`);
      if (!updatedSection || !updatedLink || token !== requestToken || !currentSection.isConnected) {
        throw new Error('The matching product section was not found.');
      }
      const metadata = readProductMetadata(parsedDocument, updatedSection, selectedProductId, destinationUrl);
      previousHead = captureHead();
      const previousLinkTop = viewportTop(link);
      const previousPickerTop = viewportTop(picker);
      preserveLayoutHeights(currentSection, updatedSection);
      document.querySelector('pickup-availability-drawer')?.remove();
      currentSection.replaceWith(updatedSection);
      sectionReplaced = true;
      applyHead(metadata);
      window.history.replaceState({}, '', destinationUrl);
      committed = true;
      if (wasExpanded) setExpanded(updatedSection.querySelector(pickerSelector), true);
      if (token === requestToken && updatedSection.isConnected && document.getElementById(`MainProduct-${sectionId}`) === updatedSection) {
        const updatedLinkTop = viewportTop(updatedLink);
        const useLink = previousLinkTop !== null && updatedLinkTop !== null;
        compensateScroll(
          useLink ? previousLinkTop : previousPickerTop,
          useLink ? updatedLinkTop : viewportTop(updatedSection.querySelector(pickerSelector))
        );
      }
      refreshRelatedProducts(selectedProductId);
      cleanDuplicateAssets();
      seedCurrentSection(updatedSection);
      scheduleViewportPrefetch(updatedSection);
      if (window.Shopify?.PaymentButton?.init) window.Shopify.PaymentButton.init();
      if (window.sakuraInitializeProductPurchaseGate) {
        window.sakuraInitializeProductPurchaseGate(updatedSection);
      }
      updatedLink.focus({ preventScroll: true });
    } catch (error) {
      if (token === requestToken) {
        if (!committed) {
          try {
            if (sectionReplaced && updatedSection.isConnected) updatedSection.replaceWith(currentSection);
            if (previousHead) applyHead(previousHead);
            if (window.location.href !== previousUrl) window.history.replaceState({}, '', previousUrl);
          } catch (restoreError) {
            // Normal navigation below remains the recovery path if DOM restoration fails.
          }
        }
        navigateNormally(destinationUrl);
      }
    } finally {
      window.clearTimeout(loadingTimer);
      if (picker.isConnected) {
        picker.classList.remove('is-loading');
        picker.setAttribute('aria-busy', 'false');
        link.classList.remove('is-loading');
      }
    }
  });

  function initialize() {
    seedCurrentSection();
    scheduleViewportPrefetch();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
